'use client'

import { useMemo } from 'react'
import clsx from 'clsx'
import Image from 'next/image'
import { Message } from '@locmod/intl'
import { constants } from 'helpers'

import { Tooltip } from 'components/feedback'
import { Button, buttonMessages } from 'components/inputs'
import { Icon, type IconName } from 'components/ui'

import projects, { Tag, type Project, type ProjectName } from './utils/projects'
import messages from './messages'


type TagProps = {
  title: Intl.Message
  accentClassName: string
  tooltip?: Intl.Message
}

const tagPropsByTagId: Record<Tag, TagProps> = {
  [Tag.DApp]: {
    title: messages.tags.dApp.title,
    accentClassName: 'bg-[#26E394] text-grey-90',
  },
  [Tag.PredictionApp]: {
    title: messages.tags.predictionsApp.title,
    accentClassName: 'bg-brand-50 text-white',
  },
  [Tag.TelegramBot]: {
    title: messages.tags.telegramBot.title,
    accentClassName: 'bg-accent-gold text-grey-90',
  },
  [Tag.Custodial]: {
    title: messages.tags.custodial.title,
    tooltip: messages.tags.custodial.tooltip,
    accentClassName: 'bg-bg-10 text-grey-90',
  },
}

type ProjectsProps = {
  className?: string
  itemClassName?: string
  filterByNames?: ProjectName[]
  withInviteCard?: boolean
}


const Projects: React.FC<ProjectsProps> = (props) => {
  const { className, itemClassName, filterByNames, withInviteCard } = props

  const items = useMemo<Project[]>(() => {
    if (filterByNames) {
      return projects.filter(({ name, hasGeoBlock }) => hasGeoBlock && filterByNames.includes(name))
    }

    return projects.filter(({ hasGeoBlock }) => hasGeoBlock)
  }, [ filterByNames?.join() ])

  const itemFinalClassName = clsx(itemClassName, 'flex flex-col p-6 -sm:px-5 bg-gradient-light bg-light rounded-8 text-grey-90')

  return (
    <ul className={className}>
      {
        items.map((item) => {
          const { image, name, description, tags, link, chains } = item
          const handleClick = () => {}

          const buttonTitle = buttonMessages.goApp
          const style = 'bgColor' in item && item.bgColor ? { backgroundColor: item.bgColor } : undefined

          return (
            <li key={name} className={itemFinalClassName}>
              <div className="flex items-start justify-between">
                <div className="flex-none flex items-center justify-center size-14 sm:size-20 bg-white rounded-4 shadow-xl" style={style}>
                  <Image className="size-14 p-2 sm:size-20 rounded-2" src={image} alt={name} />
                </div>
                <div className="flex items-center justify-end flex-wrap -m-1 ml-2 text-label-14 font-semibold font-poppins">
                  {
                    tags.map((tagId) => {
                      const { title, tooltip, accentClassName } = tagPropsByTagId[tagId]

                      const boxClassName = clsx('m-1 py-2 px-4 flex items-center whitespace-nowrap uppercase rounded-2', accentClassName)

                      if (tooltip) {
                        return (
                          <Tooltip key={tagId} placement="bottom" text={tooltip}>
                            <div className={boxClassName}>
                              <Message value={title} html />
                              <Icon className="-mt-px ml-1.5 -mr-1.5 size-3.5 opacity-25" name="interface/info-circle" />
                            </div>
                          </Tooltip>
                        )
                      }

                      return (
                        <Message
                          key={tagId}
                          className={boxClassName}
                          value={title}
                        />
                      )
                    })
                  }
                </div>
              </div>
              <Message
                className="mt-8 text-land-h5"
                value={name}
                tag="h3"
                html
              />
              <Message
                className="mt-4 as text-land-body-2"
                value={description}
                tag="div"
                html
              />
              <div className="mt-auto pt-6 flex items-center justify-between">
                {
                  Boolean(link) ? (
                    <Button
                      title={buttonTitle}
                      styling="land-black"
                      rightIcon="interface/arrow-right"
                      toTab={link}
                      size={48}
                      onClick={handleClick}
                    />
                  ) : (
                    <div className="bg-white/70 rounded-full inline-flex text-land-body-2 text-center items-center h-12 px-8 text-grey-90">
                      Coming Soon
                    </div>
                  )
                }
                <div className="flex items-center flex-row-reverse">
                  {
                    typeof chains !== 'undefined' && chains.map((chainId: Exclude<Project['chains'], undefined>[number]) => {
                      const isSolana = chainId === 'Solana'

                      const iconName: IconName = isSolana ? 'networks/solana' : constants.chainsMap[chainId].iconName
                      const chainName = isSolana ? chainId : constants.chainsMap[chainId].chainName

                      return (
                        <Tooltip key={chainId} placement="bottom" text={chainName}>
                          <div className="-ml-2 -sm:-ml-5 flex-none size-10 p-1 bg-bg-10 rounded-full shadow-lvl1">
                            <Icon
                              key={iconName}
                              className="size-full"
                              name={iconName}
                            />
                          </div>
                        </Tooltip>
                      )
                    })
                  }
                </div>
              </div>
            </li>
          )
        })
      }
      {
        withInviteCard && (
          <li className="p-6 flex flex-col rounded-8 boder border-2 border-solid border-light">
            <div className="flex items-center justify-center size-14 sm:size-20 bg-azuro-sky rounded-4 opacity-70 shadow-xl">
              <Icon name="tokens/azuro" className="size-14 p-2 sm:size-20" />
            </div>
            <Message className="mt-8 text-land-h5 text-white" value={messages.new.title} tag="h3" />
            <Message
              className="mt-4 text-land-body-2 text-azuro-sky"
              value={messages.new.text}
              tag="div"
              html
            />
            <div className="mt-auto pt-6">
              <Button
                title={messages.new.buttonTitle}
                styling="land-white"
                // toTab="https://calendly.com/azuro_team/30min"
                toTab={constants.links.frontendStart}
                rightIcon="interface/arrow-right"
                size={48}
              />
            </div>
          </li>
        )
      }
    </ul>
  )
}

export default Projects
